/* eslint-disable jsx-a11y/anchor-is-valid */
/* eslint-disable @typescript-eslint/naming-convention */
import { ReactElement, ReactNode, useEffect, useState } from 'react';

export const Nav = ({
  children,
  toggle,
}: {
  children: ReactNode;
  toggle: string;
}): ReactElement => {
  const [navbarOpen, setNavbarOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const Logo = require('../../../assets/images/logo_white.png');

  const navLinkBase =
    'relative font-semibold tracking-wide transition-all duration-300 cursor-pointer text-lg group';
  const navLinkActive = 'text-[#00D4FF]';
  const navLinkInactive =
    'text-white hover:text-[#00D4FF] hover:scale-105 hover:drop-shadow-[0_0_8px_#00D4FF]';

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const sections = document.querySelectorAll('section[id]');
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveSection(entry.target.id);
        });
      },
      { threshold: 0.5 },
    );
    sections.forEach((section) => observer.observe(section));
    return () => sections.forEach((section) => observer.unobserve(section));
  }, []);

  const goTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setNavbarOpen(false);
  };

  return (
    <>
      {/* NAVBAR */}
      <nav
        className={`fixed w-full z-50 transition-all duration-500 backdrop-blur-md ${
          scrolled ? 'bg-[#03010E]/90 shadow-[0_0_20px_rgba(0,212,255,0.2)]' : 'bg-transparent'
        }`}
      >
        <div className="flex justify-between items-center container mx-auto px-6 py-2">
          {/* Logo */}
          <div
            onClick={() => goTo('home')}
            className="cursor-pointer select-none hover:scale-105 transition-transform duration-300"
          >
            <img
              src={Logo}
              alt="Logo"
              className="w-20 md:w-20 drop-shadow-[0_0_6px_#00D4FF] hover:drop-shadow-[0_0_12px_#00D4FF] transition-all duration-300"
            />
          </div>

          {/* Desktop Menu */}
          <ul className="hidden md:flex items-center self-center space-x-10 md:mt-4">
            {[
              { id: 'about', label: 'About Us' },
              { id: 'services', label: 'Our Services' },
              { id: 'solutions', label: 'Our Solutions' },
            ].map(({ id, label }) => (
              <li
                key={id}
                onClick={() => goTo(id)}
                className={`${navLinkBase} ${
                  activeSection === id ? navLinkActive : navLinkInactive
                }`}
              >
                <span className="relative inline-block">
                  {label}
                  {/* subtle underline animation */}
                  <span className="absolute left-0 bottom-[-3px] w-0 h-[2px] text-base bg-[#00D4FF] transition-all duration-300 group-hover:w-full" />
                </span>
              </li>
            ))}
            <li>
              <button
                onClick={() => goTo('contact')}
                className={`ml-4 font-semibold rounded-full px-6 py-2 transition-all duration-300 shadow-[0_0_15px_rgba(0,212,255,0.4)] hover:scale-105 hover:shadow-[0_0_25px_rgba(0,212,255,0.6)] ${
                  activeSection === 'contact'
                    ? 'bg-[#00b8e6] text-black'
                    : 'bg-[#00D4FF] text-black hover:bg-[#00b8e6]'
                }`}
              >
                Contact&nbsp;Us
              </button>
            </li>
          </ul>

          {/* Mobile Burger */}
          <button
            onClick={() => setNavbarOpen(true)}
            className="md:hidden p-2 text-white focus:outline-none hover:text-[#00D4FF] transition-colors duration-300"
            aria-label="Open menu"
          >
            <svg
              className="w-7 h-7"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M3 6h18M3 12h18M3 18h18" />
            </svg>
          </button>
        </div>
      </nav>

      {/* 📱 MOBILE OVERLAY MENU */}
      {navbarOpen && (
        <div className="fixed inset-0 z-[60] flex flex-col items-center justify-center bg-gradient-to-b from-[#041421]/95 to-[#03010E]/95 backdrop-blur-xl animate-fadeIn">
          {/* Logo Top Left */}
          <img
            src={Logo}
            alt="Logo"
            onClick={() => goTo('home')}
            className="absolute top-6 left-6 w-24 cursor-pointer drop-shadow-[0_0_6px_#00D4FF] hover:drop-shadow-[0_0_12px_#00D4FF] transition-all duration-300"
          />

          {/* Close Button */}
          <button
            onClick={() => setNavbarOpen(false)}
            className="absolute top-6 right-6 text-white hover:text-[#00D4FF] transition-all duration-300 transform hover:rotate-90"
            aria-label="Close menu"
          >
            <svg className="w-8 h-8" viewBox="0 0 20 20" fill="currentColor">
              <path
                fillRule="evenodd"
                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
          </button>

          {/* Links */}
          <ul className="flex flex-col items-center space-y-10 text-2xl font-semibold">
            {[
              { id: 'about', label: 'About Us' },
              { id: 'services', label: 'Our Services' },
              { id: 'solutions', label: 'Our Solutions' },
            ].map(({ id, label }) => (
              <li
                key={id}
                onClick={() => goTo(id)}
                className={`${navLinkBase} ${
                  activeSection === id ? navLinkActive : navLinkInactive
                } hover:scale-110 transition-transform duration-300`}
              >
                {label}
              </li>
            ))}
            <li>
              <button
                onClick={() => goTo('contact')}
                className={`font-bold rounded-full px-10 py-4 shadow-[0_0_25px_rgba(0,212,255,0.5)] transition-all duration-300 hover:scale-110 hover:shadow-[0_0_35px_rgba(0,212,255,0.7)] ${
                  activeSection === 'contact'
                    ? 'bg-[#00b8e6] text-black'
                    : 'bg-[#00D4FF] text-black hover:bg-[#00b8e6]'
                }`}
              >
                Contact&nbsp;Us
              </button>
            </li>
          </ul>
        </div>
      )}

      {/* CONTENT */}
      <div>{children}</div>
    </>
  );
};
